import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal, Table } from 'antd';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';

import styles from './styles.module.scss';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import { useGetLocationsQuery } from '../../store/api/sensors';
import { useGetFieldsQuery } from '../../store/api/fields';
import { selectBoundary } from '../../store/slices/sensors';
import { BreakpointMobile, BreakpointMobileUp, ModalWidthSmall, PathnamesMap } from '../../constants/ui';
import { DateLlFormat, InitialEditLocationFields } from '../../constants/data';
import { useViewport } from '../../hooks';
import AddPeriodForm from '../AddPeriodForm/index.jsx';
import { LocationRemoveForm } from './index.jsx';
import { getCropName } from '../../utils/crops';
import { isDateInPeriod } from '../../utils/dates';
import { DevicesGetSeasonData, GetDevicesLocationsColumns, GetShortId } from './lib/index.jsx';
import { PageHeader } from '../PageLayout/index.jsx';

const DevicesLocations = ({ t }) => {
  const { id } = useParams();
  const { widthViewport } = useViewport();
  const { currentUserRole } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const DevicesLocationsColumns = GetDevicesLocationsColumns(t);

  const { data: locations, isSuccess: isLocationsSuccess } = useGetLocationsQuery({
    query: params.get('search'),
  });
  const { data: fields, isSuccess: isFieldsSuccess } = useGetFieldsQuery();

  const [locationsList, setLocationsList] = useState([]);
  const [sensor, setSensor] = useState({ id: '', name: '' });
  const [activeAddPeriodPanel, setActiveAddPeriodPanel] = useState(false);
  const [activeRemovePeriodModal, setActiveRemovePeriodModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(InitialEditLocationFields);
  const [locationToDelete, setLocationToDelete] = useState({ id: '', name: '' });
  const [sensorCurrentPeriods, setSensorCurrentPeriods] = useState([]);

  const onOpenAddPeriod = () => {
    let periods = [];
    dispatch(selectBoundary(''));
    locations.map((item) => {
      if (item.sensor.id === sensor.id) {
        periods.push([item.install_date, item.removal_date]);
      }
    });
    setSensorCurrentPeriods(periods);
    setSelectedLocation(InitialEditLocationFields);
    setActiveAddPeriodPanel(true);
  };

  const onOpenEditPeriod = (data) => {
    setSelectedLocation({
      locationId: data.id,
      seasonId: data.field_season_id,
      name: data.name,
      install: data.install_date,
      removal: data.removal_date,
      coordinates: data.install_point.coordinates ? data.install_point.coordinates : data.sensor.gps_point.coordinates,
      // coordinates: data.gps_point ? data.gps_point.coordinates : data.install_point.coordinates,
    });
    setActiveAddPeriodPanel(true);
  };

  const onOpenRemovePeriod = (data) => {
    setLocationToDelete({
      id: data.id,
      name: data.name,
    });
    setActiveRemovePeriodModal(true);
  };

  const handleBack = () => {
    navigate(`/${PathnamesMap.devices}`);
  };

  useEffect(() => {
    if (!id || !isLocationsSuccess || !isFieldsSuccess) return;

    const fullLocationsData = [];
    const filteredLocations = locations.filter((el) => el.sensor && el.sensor.id === id);

    if (filteredLocations && filteredLocations.length > 0) {
      filteredLocations.map((location) => {
        if (!isDateInPeriod(location.install_date, location.removal_date, dayjs())) {
          const fieldData = DevicesGetSeasonData({ fields, id: location.field_season_id });
          fullLocationsData.push({
            ...location,
            crop_id: fieldData && fieldData.season && fieldData.season.crop_id ? fieldData.season.crop_id : null,
            variety:
              fieldData && fieldData.season && fieldData.season.variety ? fieldData.season.variety : t('empty.noData'),
            field: fieldData && fieldData.field ? fieldData.field : t('empty.noField'),
          });
        }
      });
    }

    if (filteredLocations.length > 0) {
      setSensor(filteredLocations[0].sensor);
    }

    setLocationsList(fullLocationsData);
  }, [isLocationsSuccess, isFieldsSuccess]);

  return (
    <>
      {/*Header mobile*/}
      {widthViewport < BreakpointMobileUp && (
        <PageHeader
          title={t('locations.titlePrevious')}
          text={
            <>
              <strong>{sensor.name}</strong> {GetShortId(sensor.device_id)}
            </>
          }
          withBackButton={true}
          onBack={handleBack}
        >
          <Button type="primary" size="small" icon={<CroplerIcon name="add" />} onClick={() => onOpenAddPeriod()} />
        </PageHeader>
      )}

      <div className={`${styles.devices} ${styles.devicesLocations}`}>
        {/*Header*/}
        {widthViewport > BreakpointMobile && (
          <div className={styles.devicesHeader}>
            <h1 className={styles.devicesTitle}>
              {sensor.name} <span>{GetShortId(sensor.device_id)}</span>
            </h1>
            {(currentUserRole === 'owner' || currentUserRole === 'editor') && (
              <div className={styles.filter}>
                <Button type="primary" icon={<CroplerIcon name="add" />} onClick={() => onOpenAddPeriod()}>
                  {widthViewport > BreakpointMobile && t('locations.newLocation')}
                </Button>
              </div>
            )}
          </div>
        )}
        {locationsList && locationsList.length > 0 && (
          <Table
            className={`${styles.devicesTable} ${styles.devicesTableLocations} devices-table custom-scroll`}
            columns={DevicesLocationsColumns}
            dataSource={locationsList.map((item) => ({
              key: item.id,
              name: item.name,
              field: (
                <>
                  <span className={styles.devicesTableCellLabel}>{t('labels.field')}</span>
                  <span>{item.field ? item.field : t('empty.noField')}</span>
                </>
              ),
              crop: (
                <>
                  <span className={styles.devicesTableCellLabel}>{t('labels.crop')}</span>
                  <span className="text-capitalize">{getCropName({ t, id: item.crop_id })}</span>
                </>
              ),
              variety: (
                <>
                  <span className={styles.devicesTableCellLabel}>{t('labels.varietyHybrid')}</span>
                  <span>{item.variety}</span>
                </>
              ),
              period: (
                <>
                  <span className={styles.devicesTableCellLabel}>{t('labels.installedRemoved')} </span>
                  <span>{`${dayjs(item.install_date).format(DateLlFormat)} — ${dayjs(item.removal_date).format(DateLlFormat)}`}</span>
                </>
              ),
              operation: (
                <div className={styles.devicesTableNav}>
                  {(currentUserRole === 'editor' || currentUserRole === 'owner') && (
                    <>
                      <Button type="gray" size="small" onClick={() => onOpenEditPeriod(item)} icon={<CroplerIcon name="edit" />} />
                      <Button
                        type="gray"
                        size="small"
                        onClick={() => onOpenRemovePeriod({ id: item.id, name: item.name })}
                        icon={<CroplerIcon name="remove" />}
                      />
                    </>
                  )}
                </div>
              ),
            }))}
            pagination={false}
          />
        )}
      </div>

      {/*Add/Edit Location*/}
      <Modal
        title={`${t('locations.addPeriod.addPeriodFor')} ${sensor.name}`}
        open={activeAddPeriodPanel}
        onCancel={() => setActiveAddPeriodPanel(false)}
        footer={false}
        centered
        width={700}
        className="mobile-hide-modal"
      >
        {activeAddPeriodPanel && (
          <AddPeriodForm
            key={selectedLocation.locationId}
            closePanel={() => setActiveAddPeriodPanel(false)}
            sensorId={sensor.id}
            disabledPeriods={sensorCurrentPeriods}
            initialData={selectedLocation}
            t={t}
          />
        )}
      </Modal>

      {/*Delete Location*/}
      <Modal
        title={t('common.areYouSure')}
        open={activeRemovePeriodModal}
        onCancel={() => setActiveRemovePeriodModal(false)}
        footer={false}
        centered
        width={ModalWidthSmall}
      >
        <LocationRemoveForm t={t} location={locationToDelete} closeModal={() => setActiveRemovePeriodModal(false)} />
      </Modal>
    </>
  );
};

export default DevicesLocations;
